/**
 * Service to handle fetching and managing term abbreviations
 */
import { termsStore } from '../stores/termsStore.js';
import { backendService } from './backendService.js';

export class TermAbbreviationService {
    constructor(termsStore) {
        this.termsStore = termsStore;
        this.cache = new Map(); // Local cache to avoid redundant API calls
    }
    
    /**
     * Fetch abbreviations for a term from the backend
     * @param {string} term - The term to fetch abbreviations for
     * @returns {Promise<string[]>} - A promise resolving to an array of abbreviations
     */
    async getAbbreviations(term) {
        // Check if abbreviations are already in the cache
        if (this.cache.has(term)) {
            console.log(`[TermAbbreviationService] Using cached abbreviations for "${term}"`);
            return this.cache.get(term);
        }
        
        try {
            console.log(`[TermAbbreviationService] Fetching abbreviations for "${term}"`);
            
            // In development/testing, use placeholder implementation
            // TODO: Replace with actual API call in production
            if (process.env.NODE_ENV === 'development' || !backendService.hasEndpoint('fetchTermAbbreviations')) {
                console.log('[TermAbbreviationService] Using placeholder abbreviations (development mode)');
                return this.getPlaceholderAbbreviations(term);
            }
            
            // Call backend API in production
            const response = await backendService.fetchTermAbbreviations(term);
            const abbreviations = response.abbreviations || [];
            
            console.log(`[TermAbbreviationService] Received abbreviations for "${term}":`, abbreviations);
            
            // Update cache
            this.cache.set(term, abbreviations);
            
            // Update store
            this.termsStore.setAbbreviations(term, abbreviations);
            
            return abbreviations;
        } catch (error) {
            console.error(`[TermAbbreviationService] Error fetching abbreviations for "${term}":`, error);
            
            // Use placeholder abbreviations as fallback if API fails
            console.log('[TermAbbreviationService] Using placeholder abbreviations as fallback');
            return this.getPlaceholderAbbreviations(term);
        }
    }
    
    /**
     * Generate placeholder abbreviations (development only)
     * @param {string} term - The term to generate placeholder abbreviations for
     * @returns {string[]} - Array of placeholder abbreviations
     */
    getPlaceholderAbbreviations(term) {
        const words = term.split(' ');
        const abbreviations = [];
        
        // Create acronym (first letter of each word)
        if (words.length > 1) {
            const acronym = words.map(word => word[0].toUpperCase()).join('');
            abbreviations.push(acronym);
        }
        
        // First word only (if multi-word term)
        if (words.length > 1) {
            abbreviations.push(words[0]);
        }
        
        // First + last word (if 3+ words)
        if (words.length >= 3) {
            abbreviations.push(`${words[0]} ${words[words.length - 1]}`);
        }
        
        // Special case handling to demonstrate different possibilities
        if (term.toLowerCase().includes('data')) {
            abbreviations.push('info');
        }
        if (term.toLowerCase().includes('system')) {
            abbreviations.push('sys');
        }
        if (term.toLowerCase().includes('controller')) {
            abbreviations.push('ctrl');
        }
        
        console.log(`[TermAbbreviationService] Generated placeholder abbreviations for "${term}":`, abbreviations);
        
        // Update store with placeholder abbreviations
        this.termsStore.setAbbreviations(term, abbreviations);
        // Update cache
        this.cache.set(term, abbreviations);
        
        return abbreviations;
    }
    
    /**
     * Clear cached abbreviations for a term
     * @param {string} term - Term to clear abbreviations for
     */
    clearCachedAbbreviations(term) {
        this.cache.delete(term);
        this.termsStore.clearAbbreviations(term);
    }
    
    /**
     * Clear all cached abbreviations
     */
    clearAllCachedAbbreviations() {
        this.cache.clear();
        
        // We need to clear abbreviations from the store as well
        // This requires reading all terms and clearing each individually
        this.termsStore.update(store => {
            const newAbbreviationsMap = new Map();
            return {
                ...store,
                abbreviationsMap: newAbbreviationsMap
            };
        });
    }
}

// Create singleton instance
export const termAbbreviationService = new TermAbbreviationService(termsStore);